import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  ArrowRightLeft, 
  Plus, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building2, 
  User,
  Calendar,
  FileText,
  Filter,
  Eye,
  History,
  Lock,
  Settings
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import api, { employeeAPI, departmentAPI, settingsAPI } from '../lib/api';

const PromotionTransfer = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canAccess = ['admin', 'hr', 'verifier'].includes(user?.role);
  
  const [activeTab, setActiveTab] = useState('promotions');
  const [loading, setLoading] = useState(true);
  const [moduleEnabled, setModuleEnabled] = useState(null); // null = loading, true/false = loaded
  const [promotions, setPromotions] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  
  // Filter states
  const [filterStatus, setFilterStatus] = useState('');
  
  // Modal states
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Form states
  const [promotionForm, setPromotionForm] = useState({
    employee_id: '',
    new_designation: '',
    new_grade: '',
    salary_revision: '',
    effective_date: new Date().toISOString().slice(0, 10),
    remarks: ''
  });
  
  const [transferForm, setTransferForm] = useState({
    employee_id: '',
    from_department: '',
    to_department: '',
    from_location: '',
    to_location: '',
    transfer_type: 'internal',
    effective_date: new Date().toISOString().slice(0, 10),
    remarks: ''
  });

  useEffect(() => {
    checkModuleStatus();
  }, []);

  useEffect(() => {
    if (moduleEnabled) {
      fetchData();
    }
  }, [filterStatus, moduleEnabled]);

  const checkModuleStatus = async () => {
    try {
      const res = await settingsAPI.get('promotion_transfer_enabled');
      setModuleEnabled(res.data.value);
      if (res.data.value) {
        fetchData();
      }
    } catch (error) {
      // Default to disabled if error
      setModuleEnabled(false);
    }
  };

  const handleToggleModule = async (enabled) => {
    try {
      await settingsAPI.update('promotion_transfer_enabled', enabled);
      setModuleEnabled(enabled);
      toast.success(`Promotion & Transfer module ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch promotions using api module
      let promotionsUrl = '/promotions';
      if (filterStatus && filterStatus !== 'all') promotionsUrl += `?status=${filterStatus}`;
      const promotionsRes = await api.get(promotionsUrl);
      setPromotions(promotionsRes.data || []);
      
      // Fetch transfers
      let transfersUrl = '/transfers';
      if (filterStatus && filterStatus !== 'all') transfersUrl += `?status=${filterStatus}`;
      const transfersRes = await api.get(transfersUrl);
      setTransfers(transfersRes.data || []);
      
      // Fetch reference data for admins
      if (canAccess) {
        const [empRes, deptRes] = await Promise.all([
          employeeAPI.getAll(),
          departmentAPI.getAll()
        ]);
        
        setEmployees(empRes.data || []);
        setDepartments(deptRes.data || []);
        
        // Mock designations
        setDesignations([
          { id: '1', name: 'Junior Engineer' },
          { id: '2', name: 'Senior Engineer' },
          { id: '3', name: 'Lead Engineer' },
          { id: '4', name: 'Manager' },
          { id: '5', name: 'Senior Manager' },
          { id: '6', name: 'Director' },
          { id: '7', name: 'VP' }
        ]);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      // Empty states shown as fallback
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromotion = async () => {
    try {
      if (!promotionForm.employee_id || !promotionForm.new_designation || !promotionForm.effective_date) {
        toast.error('Please fill in required fields');
        return;
      }
      
      await api.post('/promotions', promotionForm);
      
      toast.success('Promotion request created successfully');
      setShowPromotionModal(false);
      setPromotionForm({
        employee_id: '',
        new_designation: '',
        new_grade: '',
        salary_revision: '',
        effective_date: new Date().toISOString().slice(0, 10),
        remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promotion request');
    }
  };

  const handleCreateTransfer = async () => {
    try {
      if (!transferForm.employee_id || !transferForm.to_department || !transferForm.effective_date) {
        toast.error('Please fill in required fields');
        return;
      }
      
      await api.post('/transfers', transferForm);
      
      toast.success('Transfer request created successfully');
      setShowTransferModal(false);
      setTransferForm({
        employee_id: '',
        from_department: '',
        to_department: '',
        from_location: '',
        to_location: '',
        transfer_type: 'internal',
        effective_date: new Date().toISOString().slice(0, 10),
        remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer request');
    }
  };

  const handleAction = async (type, id, action) => {
    try {
      const endpoint = type === 'promotion' ? 'promotions' : 'transfers';
      
      await api.put(`/${endpoint}/${id}/${action}`);
      
      toast.success(`${type === 'promotion' ? 'Promotion' : 'Transfer'} ${action}ed successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const openDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailsModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  if (loading && moduleEnabled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  // Show disabled message if module is not enabled
  if (!moduleEnabled) {
    return (
      <div data-testid="promotion-transfer-page">
        <Header 
          title="Promotion & Transfer" 
          subtitle="Manage employee promotions and transfers"
          action={
            isAdmin && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Enable Module:</span>
                  <Switch
                    checked={moduleEnabled}
                    onCheckedChange={handleToggleModule}
                    data-testid="enable-module-switch"
                  />
                </div>
              </div>
            )
          }
        />
        <div className="p-8">
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-700 mb-2">Disabled by Admin</h2>
            <p className="text-slate-500 max-w-md mb-6">
              The Promotion & Transfer module is currently disabled. Please contact your administrator to enable this feature.
            </p>
            {isAdmin && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Settings className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-blue-700">
                  As an admin, you can enable this module using the toggle in the header.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="promotion-transfer-page">
      <Header 
        title="Promotion & Transfer" 
        subtitle="Manage employee promotions and transfers"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <div className="flex items-center gap-2 mr-4 pr-4 border-r border-slate-200">
                <span className="text-sm text-slate-600">Module:</span>
                <Switch
                  checked={moduleEnabled}
                  onCheckedChange={handleToggleModule}
                  data-testid="disable-module-switch"
                />
              </div>
            )}
            {canAccess && (
              <div className="flex gap-2">
                <Button onClick={() => setShowPromotionModal(true)} data-testid="new-promotion-btn">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  New Promotion
                </Button>
                <Button variant="outline" onClick={() => setShowTransferModal(true)} data-testid="new-transfer-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  New Transfer
                </Button>
              </div>
            )}
          </div>
        }
      />
      
      <div className="p-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="dashboard-card stat-card stat-accent-primary" data-testid="pending-promotions-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Pending Promotions</p>
                <p className="text-2xl font-heading font-semibold text-slate-900">
                  {promotions.filter(p => p.status === 'pending').length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </div>
          
          <div className="dashboard-card stat-card stat-accent-success" data-testid="approved-promotions-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Approved Promotions</p>
                <p className="text-2xl font-heading font-semibold text-emerald-600">
                  {promotions.filter(p => p.status === 'approved').length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-100">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div className="dashboard-card stat-card stat-accent-accent" data-testid="pending-transfers-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Pending Transfers</p>
                <p className="text-2xl font-heading font-semibold text-blue-600">
                  {transfers.filter(t => t.status === 'pending').length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-blue-100">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="dashboard-card stat-card stat-accent-warning" data-testid="completed-transfers-stat">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Completed Transfers</p>
                <p className="text-2xl font-heading font-semibold text-purple-600">
                  {transfers.filter(t => t.status === 'completed').length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-purple-100">
                <ArrowRightLeft className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-6">
            <TabsList>
              <TabsTrigger value="promotions" className="gap-2" data-testid="promotions-tab">
                <TrendingUp className="w-4 h-4" />
                Promotions
              </TabsTrigger>
              <TabsTrigger value="transfers" className="gap-2" data-testid="transfers-tab">
                <ArrowRightLeft className="w-4 h-4" />
                Transfers
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2" data-testid="history-tab">
                <History className="w-4 h-4" />
                History
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Promotions Tab */}
          <TabsContent value="promotions">
            <div className="dashboard-card" data-testid="promotions-list">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900">
                  {isAdmin ? 'All Promotions' : 'My Promotions'}
                </h2>
                <span className="text-sm text-slate-500">{promotions.length} records</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">New Designation</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Grade</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Effective Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotions.map((promo) => (
                      <tr key={promo.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`promotion-row-${promo.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium">{promo.employee_name || 'N/A'}</p>
                              <p className="text-xs text-slate-400">{promo.employee_code || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-medium text-emerald-600">{promo.new_designation}</td>
                        <td className="py-3 px-4">{promo.new_grade || '-'}</td>
                        <td className="py-3 px-4">{formatDate(promo.effective_date)}</td>
                        <td className="py-3 px-4">{getStatusBadge(promo.status)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" onClick={() => openDetails(promo)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isAdmin && promo.status === 'pending' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-emerald-600"
                                  onClick={() => handleAction('promotion', promo.id, 'approve')}
                                  data-testid={`approve-promotion-${promo.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600"
                                  onClick={() => handleAction('promotion', promo.id, 'reject')}
                                  data-testid={`reject-promotion-${promo.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {promotions.length === 0 && (
                  <div className="text-center py-12">
                    <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No promotion records found</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Transfers Tab */}
          <TabsContent value="transfers">
            <div className="dashboard-card" data-testid="transfers-list">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900">
                  {isAdmin ? 'All Transfers' : 'My Transfers'}
                </h2>
                <span className="text-sm text-slate-500">{transfers.length} records</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">From</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">To</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Effective Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map((transfer) => (
                      <tr key={transfer.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`transfer-row-${transfer.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium">{transfer.employee_name || 'N/A'}</p>
                              <p className="text-xs text-slate-400">{transfer.employee_code || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span>{transfer.from_department || transfer.from_location || '-'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-emerald-500" />
                            <span className="text-emerald-600 font-medium">{transfer.to_department || transfer.to_location || '-'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 capitalize">{transfer.transfer_type || 'Internal'}</td>
                        <td className="py-3 px-4">{formatDate(transfer.effective_date)}</td>
                        <td className="py-3 px-4">{getStatusBadge(transfer.status)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" onClick={() => openDetails(transfer)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isAdmin && transfer.status === 'pending' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-emerald-600"
                                  onClick={() => handleAction('transfer', transfer.id, 'approve')}
                                  data-testid={`approve-transfer-${transfer.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600"
                                  onClick={() => handleAction('transfer', transfer.id, 'reject')}
                                  data-testid={`reject-transfer-${transfer.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transfers.length === 0 && (
                  <div className="text-center py-12">
                    <ArrowRightLeft className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No transfer records found</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="dashboard-card" data-testid="history-list">
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">Promotion & Transfer History</h2>
              
              <div className="space-y-4">
                {[...promotions, ...transfers]
                  .filter(r => r.status === 'completed' || r.status === 'approved')
                  .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date))
                  .map((record) => (
                    <div key={record.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className={`p-2 rounded-lg ${record.new_designation ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                        {record.new_designation ? (
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{record.employee_name || 'Employee'}</p>
                        <p className="text-sm text-slate-500">
                          {record.new_designation 
                            ? `Promoted to ${record.new_designation}`
                            : `Transferred to ${record.to_department || record.to_location}`
                          }
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">{formatDate(record.effective_date)}</p>
                        {getStatusBadge(record.status)}
                      </div>
                    </div>
                  ))
                }
                {[...promotions, ...transfers].filter(r => r.status === 'completed' || r.status === 'approved').length === 0 && (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No history records found</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* New Promotion Modal */}
        <Dialog open={showPromotionModal} onOpenChange={setShowPromotionModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                New Promotion
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Employee *</Label>
                <Select value={promotionForm.employee_id} onValueChange={(v) => setPromotionForm({...promotionForm, employee_id: v})}>
                  <SelectTrigger data-testid="promotion-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New Designation *</Label>
                <Select value={promotionForm.new_designation} onValueChange={(v) => setPromotionForm({...promotionForm, new_designation: v})}>
                  <SelectTrigger data-testid="promotion-designation-select">
                    <SelectValue placeholder="Select designation" />
                  </SelectTrigger>
                  <SelectContent>
                    {designations.map(d => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>New Grade</Label>
                  <Input
                    value={promotionForm.new_grade}
                    onChange={(e) => setPromotionForm({...promotionForm, new_grade: e.target.value})}
                    placeholder="e.g., L5"
                    data-testid="promotion-grade-input"
                  />
                </div>
                <div>
                  <Label>Salary Revision (%)</Label>
                  <Input
                    type="number"
                    value={promotionForm.salary_revision}
                    onChange={(e) => setPromotionForm({...promotionForm, salary_revision: e.target.value})}
                    placeholder="e.g., 15"
                    data-testid="promotion-salary-input"
                  />
                </div>
              </div>
              <div>
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={promotionForm.effective_date}
                  onChange={(e) => setPromotionForm({...promotionForm, effective_date: e.target.value})}
                  data-testid="promotion-date-input"
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={promotionForm.remarks}
                  onChange={(e) => setPromotionForm({...promotionForm, remarks: e.target.value})}
                  placeholder="Additional notes..."
                  rows={3}
                  data-testid="promotion-remarks-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPromotionModal(false)}>Cancel</Button>
              <Button onClick={handleCreatePromotion} data-testid="create-promotion-submit">Create Promotion</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Transfer Modal */}
        <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                New Transfer
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Employee *</Label>
                <Select value={transferForm.employee_id} onValueChange={(v) => setTransferForm({...transferForm, employee_id: v})}>
                  <SelectTrigger data-testid="transfer-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Transfer Type</Label>
                <Select value={transferForm.transfer_type} onValueChange={(v) => setTransferForm({...transferForm, transfer_type: v})}>
                  <SelectTrigger data-testid="transfer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal (Department)</SelectItem>
                    <SelectItem value="location">Location Transfer</SelectItem>
                    <SelectItem value="inter_company">Inter-Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From Department</Label>
                  <Select value={transferForm.from_department} onValueChange={(v) => setTransferForm({...transferForm, from_department: v})}>
                    <SelectTrigger data-testid="transfer-from-dept-select">
                      <SelectValue placeholder="Current dept" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>To Department *</Label>
                  <Select value={transferForm.to_department} onValueChange={(v) => setTransferForm({...transferForm, to_department: v})}>
                    <SelectTrigger data-testid="transfer-to-dept-select">
                      <SelectValue placeholder="New dept" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {transferForm.transfer_type === 'location' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>From Location</Label>
                    <Input
                      value={transferForm.from_location}
                      onChange={(e) => setTransferForm({...transferForm, from_location: e.target.value})}
                      placeholder="Current location"
                      data-testid="transfer-from-location-input"
                    />
                  </div>
                  <div>
                    <Label>To Location</Label>
                    <Input
                      value={transferForm.to_location}
                      onChange={(e) => setTransferForm({...transferForm, to_location: e.target.value})}
                      placeholder="New location"
                      data-testid="transfer-to-location-input"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={transferForm.effective_date}
                  onChange={(e) => setTransferForm({...transferForm, effective_date: e.target.value})}
                  data-testid="transfer-date-input"
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={transferForm.remarks}
                  onChange={(e) => setTransferForm({...transferForm, remarks: e.target.value})}
                  placeholder="Additional notes..."
                  rows={3}
                  data-testid="transfer-remarks-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferModal(false)}>Cancel</Button>
              <Button onClick={handleCreateTransfer} data-testid="create-transfer-submit">Create Transfer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Details Modal */}
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Record Details
              </DialogTitle>
            </DialogHeader>
            {selectedRecord && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedRecord.employee_name || 'N/A'}</p>
                    <p className="text-sm text-slate-500">{selectedRecord.employee_code || ''}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <p className="mt-1">{getStatusBadge(selectedRecord.status)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Effective Date</p>
                    <p className="font-medium mt-1">{formatDate(selectedRecord.effective_date)}</p>
                  </div>
                </div>
                
                {selectedRecord.new_designation && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">New Designation</p>
                      <p className="font-medium text-emerald-600 mt-1">{selectedRecord.new_designation}</p>
                    </div>
                    {selectedRecord.new_grade && (
                      <div>
                        <p className="text-sm text-slate-500">New Grade</p>
                        <p className="font-medium mt-1">{selectedRecord.new_grade}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedRecord.to_department && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">From</p>
                      <p className="font-medium mt-1">{selectedRecord.from_department || selectedRecord.from_location || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">To</p>
                      <p className="font-medium text-emerald-600 mt-1">{selectedRecord.to_department || selectedRecord.to_location}</p>
                    </div>
                  </div>
                )}
                
                {selectedRecord.remarks && (
                  <div>
                    <p className="text-sm text-slate-500">Remarks</p>
                    <p className="mt-1 p-3 bg-slate-50 rounded-lg text-sm">{selectedRecord.remarks}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailsModal(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PromotionTransfer;
